import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

export interface OptimizationConfig {
  maxEnergyKwh: number;
  maxPowerKw: number;
  minEnergyKwh: number;
  minPowerKw: number;
  bessCapacityKwh: number;
  bessPowerKw: number;
  roundTripEfficiency: number;
  depthOfDischarge: number;
  degradationRate: number;
  projectLifetime: number;
  maxDailyChargeCycles: number;
  maxDailyDischargeCycles: number;
  maxGridConnectionKw: number;
  generatorEnabled?: boolean;
  generatorCostPerHour?: number;
  generatorHoursPerDay?: number;
  // BESS Sizing Mode
  autoSize?: boolean;
  fixedPowerKw?: number;
  fixedEnergyKwh?: number;
  cRate?: number;
  energyToMaxDemandRatio?: number;
  // Retail Tariff Cap
  retailTariffCapPerKwh?: number;
  // Demand Charges
  demandChargePerKw?: number;
  generatorCostPerKwh?: number;
  // Other fields
  useGenerator?: boolean;
  includeSensitivity?: boolean;
  bessStrategy?: string;
  algorithmMode?: 'auto' | 'manual';
  selectedAlgorithm?: 'perfect_foresight' | 'perfect_foresight_new' | 'perfect_foresight_peak_shave' | 'arbitrage_priority' | 'peak_zero_daily' | 'peak_zero_multiday';
  id?: number;
  projectId?: string;
  createdAt?: string;
  active?: boolean;
}

interface ConfigurationResponse {
  success: boolean;
  data: OptimizationConfig;
}

/**
 * Hook to manage configuration data via backend API
 * Replaces Zustand store for configuration management
 */
export function useConfiguration(projectId: string | undefined) {
  const queryClient = useQueryClient();

  // GET configuration from backend
  const { data, isLoading, error, refetch } = useQuery<ConfigurationResponse>({
    queryKey: ['project', projectId, 'configuration'],
    queryFn: async () => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await fetch(`${API_BASE_URL}/optimization/configuration/${projectId}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }
      const result = await response.json();

      // Flatten nested configuration from backend
      if (result.data) {
        result.data = flattenConfiguration(result.data);
      }

      return result;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (formerly cacheTime)
  });

  // POST/UPDATE configuration to backend
  const updateConfiguration = useMutation<ConfigurationResponse, Error, Partial<OptimizationConfig>>({
    mutationFn: async (config) => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await fetch(`${API_BASE_URL}/optimization/configuration/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useConfiguration] Backend error:', errorText);
        throw new Error('Failed to save configuration');
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      // Invalidate and refetch configuration
      queryClient.invalidateQueries({ queryKey: ['project', projectId, 'configuration'] });
      // Don't show toast here - let the calling component handle it
    },
    onError: (error) => {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration');
    }
  });

  // DELETE configuration from backend (if needed)
  const deleteConfiguration = useMutation<void, Error>({
    mutationFn: async () => {
      if (!projectId) throw new Error('No project ID provided');

      const response = await fetch(`${API_BASE_URL}/optimization/configuration/${projectId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete configuration');
      }
    },
    onSuccess: () => {
      // Invalidate configuration
      queryClient.invalidateQueries({ queryKey: ['project', projectId, 'configuration'] });
      toast.success('Configuration deleted');
    },
    onError: (error) => {
      console.error('Failed to delete configuration:', error);
      toast.error('Failed to delete configuration');
    }
  });

  return {
    configuration: data?.data || null,
    isLoading,
    error,
    updateConfiguration,
    deleteConfiguration,
    refetch
  };
}

/**
 * Helper hook to get just the configuration without mutation functions
 * Useful for read-only components
 */
export function useReadConfiguration(projectId: string | undefined) {
  const { configuration, isLoading, error } = useConfiguration(projectId);
  return { configuration, isLoading, error };
}

/**
 * Flatten a configuration from backend format (nested) to frontend format (flat)
 */
function flattenConfiguration(config: any): any {
  const flatConfig: any = {};

  // Flatten financial parameters
  if (config.financial) {
    Object.assign(flatConfig, config.financial);
    if (config.financial.projectLifetime !== undefined) {
      flatConfig.projectLifetime = config.financial.projectLifetime;
    }
  }

  // Flatten technical parameters
  if (config.technical) {
    Object.assign(flatConfig, config.technical);
  }

  // Flatten BESS range parameters
  if (config.bessRange) {
    Object.assign(flatConfig, config.bessRange);
  }

  // Flatten grid parameters
  if (config.grid) {
    flatConfig.maxGridConnectionKw = config.grid.maxConnectionKw;
  }

  // Flatten generator configuration
  if (config.generator) {
    flatConfig.generatorEnabled = config.generator.useGenerator;
    flatConfig.useGenerator = config.generator.useGenerator;
    flatConfig.generatorCostPerHour = config.generator.costPerHour;
    flatConfig.generatorHoursPerDay = config.generator.hoursPerDay;
    flatConfig.generatorPowerKw = config.generator.powerKw;
    flatConfig.generatorCostPerKwh = config.generator.costPerKwh;
  }

  // Flatten BESS sizing mode
  if (config.sizingMode) {
    flatConfig.autoSize = config.sizingMode.autoSize;
    flatConfig.fixedPowerKw = config.sizingMode.fixedPowerKw;
    flatConfig.fixedEnergyKwh = config.sizingMode.fixedEnergyKwh;
    flatConfig.cRate = config.sizingMode.cRate;
    flatConfig.energyToMaxDemandRatio = config.sizingMode.energyToMaxDemandRatio;
  }

  // Flatten retail tariff and demand charges
  if (config.retailTariff) {
    flatConfig.retailTariffCapPerKwh = config.retailTariff.capPerKwh;
  }
  if (config.demandCharge) {
    flatConfig.demandChargePerKw = config.demandCharge.perKw;
  }

  // Flatten financing parameters: prefer nested 'financing' object (from DB), fall back to top-level keys
  const fin = config.financing;
  flatConfig.enableFinancing = fin?.enableFinancing ?? config.enableFinancing;
  flatConfig.leasingRate = fin?.leasingRate ?? config.leasingRate;
  flatConfig.leasingYears = fin?.leasingYears ?? config.leasingYears;
  flatConfig.downPaymentPercent = fin?.downPaymentPercent ?? config.downPaymentPercent;
  if (config.leasingCurrency !== undefined) {
    flatConfig.leasingCurrency = config.leasingCurrency;
  }
  flatConfig.financingCurrency = fin?.financingCurrency ?? config.financingCurrency;
  flatConfig.ufLeasingRate = fin?.ufLeasingRate ?? config.ufLeasingRate;
  flatConfig.expectedInflation = fin?.expectedInflation ?? config.expectedInflation;

  // Flatten price contract fields
  if (config.priceContract) {
    flatConfig.contractType = config.priceContract.contractType;
    flatConfig.useTimeOfUsePricing = config.priceContract.useTimeOfUsePricing;

    if (config.priceContract.dayPeriod) {
      flatConfig.dayPeriodStartHour = config.priceContract.dayPeriod.startHour;
      flatConfig.dayPeriodEndHour = config.priceContract.dayPeriod.endHour;
      flatConfig.dayPeriodCapUsdKwh = config.priceContract.dayPeriod.capUsdKwh;
    }

    if (config.priceContract.nightPeriod) {
      flatConfig.nightPeriodCapUsdKwh = config.priceContract.nightPeriod.capUsdKwh;
    }

    if (config.priceContract.spotPriceAdjustment) {
      flatConfig.spotPriceSpreadUsdKwh = config.priceContract.spotPriceAdjustment.spreadUsdKwh;
      flatConfig.spotPriceSpreadPercent = config.priceContract.spotPriceAdjustment.spreadPercent;
    }

    // Optional phase 2 contract
    flatConfig.secondaryContract = config.priceContract.secondaryContract ?? null;
    flatConfig.secondaryContractStartYear = config.priceContract.secondaryContractStartYear ?? null;
  }

  // Add top-level fields
  flatConfig.id = config.id;
  flatConfig.projectId = config.projectId;
  flatConfig.createdAt = config.createdAt;
  flatConfig.active = config.active;
  flatConfig.includeSensitivity = config.includeSensitivity;
  flatConfig.bessStrategy = config.bessStrategy;

  flatConfig.algorithmMode = config.algorithm?.algorithmMode;
  flatConfig.selectedAlgorithm = config.algorithm?.selectedAlgorithm;

  return flatConfig;
}

/**
 * Hook to fetch all configurations for a project (including inactive ones)
 */
export function useAllConfigurations(projectId: string | undefined) {
  return useQuery<OptimizationConfig[]>({
    queryKey: ['project', projectId, 'configurations', 'all'],
    queryFn: async () => {
      if (!projectId) throw new Error('No project ID provided');

      console.log('[useAllConfigurations] Fetching all configurations for project:', projectId);

      const response = await fetch(`${API_BASE_URL}/optimization/configurations/${projectId}/all`, {
        credentials: 'include'
      });
      if (!response.ok) {
        console.log('[useAllConfigurations] Failed to fetch:', response.status);
        throw new Error('Failed to fetch configurations');
      }
      const result = await response.json();
      console.log('[useAllConfigurations] Received raw configurations:', result.data);

      // Flatten all configurations
      const flattenedConfigs = (result.data || []).map((config: any) => flattenConfiguration(config));

      console.log('[useAllConfigurations] Flattened configurations:', flattenedConfigs);
      if (flattenedConfigs.length > 0) {
        console.log('[useAllConfigurations] First flattened config:', {
          id: flattenedConfigs[0].id,
          useGenerator: flattenedConfigs[0].useGenerator,
          contractType: flattenedConfigs[0].contractType,
          autoSize: flattenedConfigs[0].autoSize,
          fixedPowerKw: flattenedConfigs[0].fixedPowerKw,
          dayPeriodCapUsdKwh: flattenedConfigs[0].dayPeriodCapUsdKwh
        });
      }

      return flattenedConfigs;
    },
    enabled: !!projectId,
    staleTime: 1 * 60 * 1000, // Fresh for 1 minute
  });
}